import { EmscriptenModule, XMLFileType } from '../types';
import { RESERVED_NAME_KEYWORDS, RESERVED_PATH_KEYWORDS, VALID_C_VAR_NAME } from './constants';
import { normalizePath } from '../utils/path';

export type ProcessedXMLForEmscripten = {
    xmlDataWithoutComments: string;
    runtimeInstance: EmscriptenModule;
};

export type PointerResult = {
    arrayPtr: number;
    numStrings: number;
    stringArray: string[];
};

export const removeXMLComments = (xmlToProcess: string): string => xmlToProcess.replace(/<!--[\s\S]*?-->/g, '');

export const parseAndProcessXMLForEmscripten = async (
    xmlToProcess: string,
    runtimeInstance: EmscriptenModule,
    componentName: string,
    displayStyle: Record<string, string>,
    displayLanguage: string,
    fileType?: XMLFileType
): Promise<ProcessedXMLForEmscripten> => {
    const xmlDataWithoutComments: string = removeXMLComments(xmlToProcess);
    const { arrayPtr, numStrings, stringArray } = convertToCharPointers(displayStyle, runtimeInstance);

    runtimeInstance.lvrt_process_data?.(
        xmlDataWithoutComments,
        componentName,
        arrayPtr,
        fileType || 'component',
        displayLanguage
    );

    cleanupMemoryFromPointers(numStrings, stringArray, runtimeInstance, arrayPtr);

    return {
        xmlDataWithoutComments,
        runtimeInstance
    };
};

export const convertToCharPointers = (
    object: Record<string, string>,
    runtimeInstance: EmscriptenModule
): PointerResult => {
    // Collect record key-value pairs into an array
    const stringArray: string[] = Object.entries(object).reduce<string[]>((acc, [key, value]) => {
        acc.push(key, value);
        return acc;
    }, []);

    // Add NULL terminator pair
    stringArray.push('', '');

    // Create the array in WASM memory
    const numStrings: number = stringArray.length;

    // Allocate memory for the array of pointers
    const arrayPtr: number = runtimeInstance._malloc(numStrings * 4);

    // For each string allocate memory and store pointer
    for (let i = 0; i < numStrings; i++) {
        const str: string = stringArray[i];
        if (str) {
            // Convert string to UTF8
            const strBuffer: Uint8Array<ArrayBuffer> = new TextEncoder().encode(str + '\0'); // Null-terminated
            const strPtr: number = runtimeInstance._malloc(strBuffer.length);

            // Copy string to WASM memory
            const heap: Uint8Array<ArrayBufferLike> = new Uint8Array(
                runtimeInstance.HEAPU8.buffer,
                strPtr,
                strBuffer.length
            );
            heap.set(strBuffer);

            // Store pointer in array
            runtimeInstance.setValue(arrayPtr + i * 4, strPtr, 'i32');
        } else {
            // Store NULL pointer
            runtimeInstance.setValue(arrayPtr + i * 4, 0, 'i32');
        }
    }

    return { arrayPtr, numStrings, stringArray };
};

// Cleanup the memory allocated for the style array
export const cleanupMemoryFromPointers = (
    numStrings: number,
    stringArray: string[],
    runtimeInstance: EmscriptenModule,
    arrayPtr: number
): void => {
    const strPtrs: number[] = [];
    for (let i = 0; i < numStrings; i++) {
        const str: string = stringArray[i];
        if (str) {
            const strBuffer: Uint8Array<ArrayBuffer> = new TextEncoder().encode(str + '\0');
            const strPtr: number = runtimeInstance._malloc(strBuffer.length);
            new Uint8Array(runtimeInstance.HEAPU8.buffer, strPtr, strBuffer.length).set(strBuffer);

            runtimeInstance.setValue(arrayPtr + i * 4, strPtr, 'i32');
            strPtrs.push(strPtr);
        } else {
            runtimeInstance.setValue(arrayPtr + i * 4, 0, 'i32');
        }
    }

    strPtrs.forEach((ptr: number) => runtimeInstance._free(ptr));
    runtimeInstance._free(arrayPtr);
};

export const isValidCIdentifier = (name: string): boolean =>
    VALID_C_VAR_NAME.test(name) && !RESERVED_NAME_KEYWORDS.has(name) && !RESERVED_PATH_KEYWORDS.has(name);

export const isValidFolderName = (name: string): boolean =>
    VALID_C_VAR_NAME.test(name) && !RESERVED_NAME_KEYWORDS.has(name);

export const isValidCIdentifierFilePath = (filePath: string): boolean => {
    if (!filePath) return false;
    const normalizedPath: string = normalizePath(filePath);
    const baseName: string =
        normalizedPath
            .replace(/\.[^/.]+$/, '')
            .split('/')
            .pop() || '';

    return isValidCIdentifier(baseName);
};

export const getUniqueCIdentifierCopyFileName = (fileName: string, existingNames: ReadonlySet<string>): string => {
    const extensionMatch: RegExpMatchArray | null = fileName.match(/\.[^/.]+$/);
    const extension: string = extensionMatch?.[0] ?? '';
    const baseName: string = fileName.slice(0, fileName.length - extension.length);
    const copyBaseName: string = baseName.replace(/_copy(?:_[0-9]+)?$/, '');
    let index: number = 1;
    let candidate: string = `${copyBaseName}_copy${extension}`;

    while (existingNames.has(candidate)) {
        index += 1;
        candidate = `${copyBaseName}_copy_${index}${extension}`;
    }

    return candidate;
};

// NOTE: The 'name' should be the filename WITHOUT the extension
export const validVariableNameFromFilename = (name: string): string =>
    name
        .replace(/[^a-zA-Z0-9_]/g, '_') // Replace special characters with underscore
        .replace(/^[0-9]+/, '_') // Replace any leading digits with an underscore
        .replace(/_{2,}/g, '_'); // Remove multiple underscores

export const getProjectName = (projectPath: string, project?: Document, nameAttribute?: string): string =>
    validVariableNameFromFilename(
        nameAttribute ||
            project?.documentElement?.getAttribute('name') ||
            normalizePath(projectPath).split('/').pop() ||
            'unknown_project_name'
    );

export const determineRuntimeInitFunctionName = (projectName: string): string => `${projectName}_init`;

export const determineProjectSetTargetFunctionName = (projectName: string): string => `${projectName}_set_target`;

export const base64ToArrayBuffer = (base64: string): ArrayBuffer => {
    const binaryString: string = atob(base64);
    const length: number = binaryString.length;
    const bytes: Uint8Array<ArrayBuffer> = new Uint8Array(length);
    for (let i = 0; i < length; i++) bytes[i] = binaryString.charCodeAt(i);
    return bytes.buffer;
};

export const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
    let binary: string = '';
    const bytes: Uint8Array<ArrayBuffer> = new Uint8Array(buffer);
    const length: number = bytes.byteLength;
    for (let i = 0; i < length; i++) binary += String.fromCharCode(bytes[i]);
    return btoa(binary);
};

export const destringifyResponse = (s: string): Response => {
    const p: Response = JSON.parse(s);
    return new Response(base64ToArrayBuffer(p.body as unknown as string), {
        status: p.status,
        statusText: p.statusText,
        headers: p.headers
    });
};

// NOTE: Does not handle redirected, type or URL properties of Response
export const stringifyResponse = async (r: Response): Promise<string> => {
    const plainHeaders: Record<string, string> = {};
    for (const [key, value] of r.headers.entries()) plainHeaders[key] = value;

    const body: ArrayBuffer = await r.arrayBuffer();

    return JSON.stringify({
        status: r.status,
        statusText: r.statusText,
        headers: plainHeaders,
        body: arrayBufferToBase64(body)
    });
};

export const serializeError = (error: Error): string => {
    return JSON.stringify(error, (_, value) => {
        if (value instanceof Error) {
            const errorEntries = Object.getOwnPropertyNames(value).map((prop) => [
                prop,
                (value as unknown as Record<string, unknown>)[prop]
            ]);
            return Object.fromEntries(errorEntries);
        }
        return value;
    });
};

// NOTE: When logging errors created by the function, the most descriptive text is in error stack
export const deserializeError = (serializedError: string): Error => {
    const jsonError: { error?: string; stack?: string; cause?: unknown } = JSON.parse(serializedError);
    const error = new Error(jsonError.error);
    if (jsonError.cause !== undefined) (error as Error & { cause?: unknown }).cause = jsonError.cause;
    if (jsonError.stack) error.stack = jsonError.stack;

    return error;
};

const FNV32_OFFSET = 0x811c9dc5;
const FNV32_PRIME = 0x01000193;

// FNV-1a 32-bit hash function for strings, returning a hex string
export const fnv1a32StringHashHex = (str: string): string => {
    const data: Uint8Array = new TextEncoder().encode(str);
    let hash = FNV32_OFFSET;
    for (let i = 0; i < data.length; i++) {
        hash ^= data[i];
        hash = Math.imul(hash, FNV32_PRIME) >>> 0;
    }
    return hash.toString(16).padStart(8, '0');
};

const FNV64_OFFSET = 0xcbf29ce484222325n;
const FNV64_PRIME = 0x100000001b3n;
const FNV64_MASK = 0xffffffffffffffffn;

// FNV-1a 64-bit hash function for strings, returning a hex string
export const fnv1a64StringHashHex = (str: string): string => {
    const data: Uint8Array = new TextEncoder().encode(str);
    let hash = FNV64_OFFSET;
    const prime = FNV64_PRIME;

    for (let i = 0; i < data.length; i++) {
        hash ^= BigInt(data[i]);
        hash = (hash * prime) & FNV64_MASK;
    }

    return hash.toString(16).padStart(16, '0');
};
